import { AuditStatusSplitButton } from "@/components/audits/AuditStatusSplitButton";
import { UploadSheet } from "@/components/documents/upload/UploadSheet";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Dropdown } from "@/components/ui/Dropdown";
import { ViewToggle } from "@/components/ui/PageHeader/ViewToggle";
import { Button } from "@/components/ui/primitives";
import { useApiMutation } from "@/packages/features-shared";
import type { UIAudit, Id } from "@/packages/types";
import { useNavigate, useParams } from "@tanstack/react-router";
import { api } from "convex/_generated/api";
import {
  Archive,
  ArrowLeft,
  CloudUpload,
  Download,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "@/packages/utils";
import { AuditEditSheet } from "./AuditEditSheet";
import { useAuditStatusChange } from "./use-audit-status-change";
import { useTabNavigation } from "./AuditDetailTabs";

interface AuditDetailHeaderProps {
  audit: UIAudit;
  onBack: () => void;
  uploadSheetOpen?: boolean;
  onOpenUpload?: () => void;
  onCloseUpload?: () => void;
  editSheetOpen?: boolean;
  onOpenEdit?: () => void;
  onCloseEdit?: () => void;
}

export function AuditDetailHeader({
  audit,
  onBack,
  uploadSheetOpen: externalUploadOpen,
  onOpenUpload,
  onCloseUpload,
  editSheetOpen: externalEditOpen,
  onOpenEdit,
  onCloseEdit,
}: AuditDetailHeaderProps) {
  const { t } = useTranslation(["audits", "common"]);
  const [internalEditSheetOpen, setInternalEditSheetOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [internalUploadSheetOpen, setInternalUploadSheetOpen] = useState(false);
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  const isEditSheetOpen = externalEditOpen ?? internalEditSheetOpen;
  const setIsEditSheetOpen = (open: boolean) => {
    if (open) {
      onOpenEdit ? onOpenEdit() : setInternalEditSheetOpen(true);
    } else {
      onCloseEdit ? onCloseEdit() : setInternalEditSheetOpen(false);
    }
  };

  const uploadSheetOpen = externalUploadOpen ?? internalUploadSheetOpen;
  const setUploadSheetOpen = (open: boolean) => {
    if (open) {
      onOpenUpload ? onOpenUpload() : setInternalUploadSheetOpen(true);
    } else {
      onCloseUpload ? onCloseUpload() : setInternalUploadSheetOpen(false);
    }
  };

  const { execute: executeStatusChange, isPending: isArchiving } =
    useAuditStatusChange();

  const deleteMutation = useApiMutation(api.audit_mutations.deleteAudit, {
    name: "delete-audit",
    successMessage: t("audits:status.deleted"),
  });

  const handleArchiveConfirm = async () => {
    await executeStatusChange(
      "archive",
      audit.id as Parameters<typeof executeStatusChange>[1],
    );
    setArchiveConfirmOpen(false);
    navigate({
      to: "/$workspaceSlug/audits",
      params: { workspaceSlug: workspaceSlug ?? "" },
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync({
        auditId: audit.id as Parameters<
          typeof deleteMutation.mutateAsync
        >[0]["auditId"],
      });
      setDeleteConfirmOpen(false);
      navigate({
        to: "/$workspaceSlug/audits",
        params: { workspaceSlug: workspaceSlug ?? "" },
      });
    } catch {
      // Error is already shown via useApiMutation's onError toast
      setDeleteConfirmOpen(false);
    }
  };
  const { viewOptions, handleViewChange } = useTabNavigation({
    showSoaTab: audit.soaStatus != null,
  });

  return (
    <>
      <div className="shrink-0 pb-3">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Back button */}
          <button
            type="button"
            onClick={onBack}
            className="text-secondary hover:text-primary transition-colors p-1 -ml-1 rounded-button hover:bg-card-hover"
          >
            <ArrowLeft size={18} />
          </button>

          {/* Title */}
          <h1 className="text-lg font-semibold text-primary truncate min-w-0">
            {audit.displayName}
          </h1>

          {/* Tabs (dropdown on small screens, tab bar on xl+) */}
          <div className="xl:flex-1 flex xl:justify-center min-w-0 shrink-0 xl:shrink">
            <ViewToggle options={viewOptions} onViewChange={handleViewChange} />
          </div>

          {/* Spacer pushes actions right on small screens */}
          <div className="flex-1 xl:hidden" />

          {/* Status split button */}
          <AuditStatusSplitButton audit={audit} />

          {/* Overflow menu */}
          <Dropdown>
            <Dropdown.Trigger>
              <Button variant="ghost" icon={MoreHorizontal} iconOnly />
            </Dropdown.Trigger>
            <Dropdown.Content align="end">
              <Dropdown.Item
                icon={<CloudUpload size={14} />}
                onSelect={() => setUploadSheetOpen(true)}
              >
                {t("common:buttons.upload")}
              </Dropdown.Item>
              <Dropdown.Item
                icon={<Pencil size={14} />}
                onSelect={() => setIsEditSheetOpen(true)}
              >
                {t("audits:actions.edit")}
              </Dropdown.Item>
              <Dropdown.Item icon={<Download size={14} />} onSelect={() => {}}>
                {t("audits:actions.export")}
              </Dropdown.Item>
              <Dropdown.Separator />
              <Dropdown.Item
                icon={<Archive size={14} />}
                onSelect={() => setArchiveConfirmOpen(true)}
              >
                {t("audits:actions.archive")}
              </Dropdown.Item>
              <Dropdown.Item
                destructive
                icon={<Trash2 size={14} />}
                onSelect={() => setDeleteConfirmOpen(true)}
              >
                {t("audits:actions.delete")}
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown>
        </div>
      </div>

      <AuditEditSheet
        audit={audit}
        open={isEditSheetOpen}
        onClose={() => setIsEditSheetOpen(false)}
      />

      <UploadSheet
        open={uploadSheetOpen}
        onClose={() => setUploadSheetOpen(false)}
        preSelectedAudit={{
          id: audit.id as Id<"audits">,
          displayName: audit.displayName,
          code: audit.code,
        }}
      />

      {/* Archive confirmation dialog */}
      <ConfirmDialog
        open={archiveConfirmOpen}
        onClose={() => setArchiveConfirmOpen(false)}
      >
        <ConfirmDialog.Title>
          {t("audits:actions.archiveConfirm.title")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-sm text-secondary">
            {t("audits:actions.archiveConfirm.body", {
              name: audit.displayName,
            })}
          </p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleArchiveConfirm}
          confirmLabel={t("audits:actions.archiveConfirm.confirm")}
          loading={isArchiving}
        />
      </ConfirmDialog>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <ConfirmDialog.Title>
          {t("audits:actions.deleteConfirm.title")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-sm text-secondary">
            {t("audits:actions.deleteConfirm.body", {
              name: audit.displayName,
            })}
          </p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleDeleteConfirm}
          confirmLabel={t("audits:actions.deleteConfirm.confirm")}
          variant="danger"
          loading={deleteMutation.isPending}
        />
      </ConfirmDialog>
    </>
  );
}
